import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { fileFilingTarget } from "@msuite/utils/files";
import { type FileFilingSchema, fileFilingSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { fileInboxEntry } from "@/api/files";
import { FileFilingForm } from "@/forms/file-filing-form";

/** Props Interface */
interface FileFilingModalProps extends ModalProps {
	fileId: string;
	fileName: string;
	onComplete?: () => Promise<unknown>;
}

/**
 * Files one inbox file onto a record the person picks.
 *
 * This is the picker behind the classifier's own guesses: those are one click each on the row, and
 * this is what happens when none of them is right — or when there were none, which is every file
 * until the classifier can read it.
 */
export function FileFilingModal({
	isOpen,
	onClose,
	onComplete,
	fileId,
	fileName,
}: FileFilingModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm<FileFilingSchema>({
		resolver: resolver(fileFilingSchema),
		defaultValues: { collection: "projectDocumentFiles", projectId: "", recordId: null },
	});

	/** Functions */
	async function handleOnSubmit(json: FileFilingSchema) {
		const foreignKeyValues = fileFilingTarget(json.collection, json.projectId, json.recordId);
		if (!foreignKeyValues) return;
		await fileInboxEntry(fileId, json.collection, foreignKeyValues);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("files.inbox_file_it", { file: fileName })}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<FileFilingForm />
				</Modal.Body>
				<Modal.FooterForm submitLabel={t("files.inbox_file_it_action")} />
			</Modal>
		</FormProvider>
	);
}
